/**
 * pricing — markup × margem de lucro, em módulo PURO.
 *
 * POR QUE ISTO NÃO MORA NO COMPONENTE: as duas fórmulas são parecidas o
 * bastante para serem trocadas sem ninguém notar, e o erro é invisível — o
 * número sai plausível e só o lucro real do prestador é que fica errado.
 *
 *   markup % = (venda − custo) / CUSTO  × 100   ← sobre o que você PAGOU
 *   margem % = (venda − custo) / VENDA  × 100   ← sobre o que você RECEBEU
 *
 * Um custo de R$ 100 vendido a R$ 150 tem markup de 50% e margem de 33,3%.
 * Quem calcula "50% de margem" aplicando markup de 50% perde 17 pontos de
 * lucro em cada venda, e a conta fecha na planilha — por isso a calculadora
 * mostra sempre os DOIS indicadores, nunca só o que o usuário pediu.
 *
 * Os casos degenerados (custo 0, margem ≥ 100%) são tratados aqui e devolvem
 * `null`/erro, nunca `Infinity` nem `NaN` — que iriam parar na tela.
 */

/** Qual percentual o usuário está digitando. */
export type PricingMode = 'markup' | 'margin';

export interface PricingResult {
  /** `false` quando não dá para calcular — `error` explica em pt-BR. */
  ok: boolean;
  error: string | null;
  /** Preço de venda (arredondado em centavos). */
  price: number | null;
  /** Lucro em R$ (venda − custo), coerente com o preço arredondado. */
  profit: number | null;
  /** Indicador de markup — `null` quando indefinido (custo zero). */
  markupPercent: number | null;
  /** Indicador de margem — `null` quando indefinido (venda zero). */
  marginPercent: number | null;
}

export const MARKUP_HELP =
  'Markup é quanto você acrescenta sobre o CUSTO. Margem é quanto do PREÇO DE VENDA sobra de lucro. ' +
  'Custo R$ 100 vendido a R$ 150: markup de 50%, margem de 33,3%.';

/** Arredonda para centavos sem o erro de ponto flutuante do `toFixed` puro. */
export function roundMoney(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/** Arredonda um percentual para 2 casas (o que a tela mostra). */
export function roundPercent(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Lê um percentual digitado em pt-BR ("33,3"). `null` quando não é número.
 * Vazio também é `null` — a calculadora trata como "ainda não informado".
 */
export function parsePercentInput(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const text = String(raw ?? '').trim();
  if (!text) return null;
  // Estrito: `parseFloat('50abc')` devolvia 50 — lia metade do que o usuário
  // digitou e seguia em frente. Um separador só, vírgula ou ponto.
  if (!/^-?\d+([.,]\d+)?$/.test(text)) return null;
  const n = Number(text.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Preço a partir do custo e do markup: venda = custo × (1 + markup/100). */
export function priceFromMarkup(cost: number, markupPercent: number): number {
  return roundMoney(cost * (1 + markupPercent / 100));
}

/**
 * Preço a partir do custo e da margem: venda = custo / (1 − margem/100).
 * A divisão é o que torna margem ≥ 100% impossível (denominador ≤ 0).
 */
export function priceFromMargin(cost: number, marginPercent: number): number {
  return roundMoney(cost / (1 - marginPercent / 100));
}

/**
 * Markup implícito de um par (custo, venda). `null` quando o custo é ZERO —
 * é uma divisão por zero, e "markup infinito" não é informação: um item que
 * não custa nada não tem sobre o que ser remarcado.
 */
export function markupFromPrice(cost: number, price: number): number | null {
  if (!Number.isFinite(cost) || !Number.isFinite(price)) return null;
  if (cost <= 0) return null;
  return roundPercent(((price - cost) / cost) * 100);
}

/**
 * Margem implícita de um par (custo, venda). `null` quando a venda é zero.
 * Com custo zero e venda positiva a margem é 100% — e isso é definido, não
 * degenerado: tudo o que entrou é lucro.
 */
export function marginFromPrice(cost: number, price: number): number | null {
  if (!Number.isFinite(cost) || !Number.isFinite(price)) return null;
  if (price <= 0) return null;
  return roundPercent(((price - cost) / price) * 100);
}

/** Mensagens de erro (pt-BR) — declaradas para os testes poderem afirmá-las. */
export const PRICING_ERRORS = {
  noCost: 'Informe o custo para calcular.',
  invalidCost: 'Informe um custo válido.',
  zeroCost:
    'Com custo zero não há markup nem margem a calcular (todo o preço vira lucro). Informe o preço direto.',
  noPercent: 'Informe o percentual.',
  negativePercent: 'O percentual não pode ser negativo.',
  marginTooHigh: 'A margem precisa ser menor que 100% — não existe venda com 100% de margem sobre um custo.',
  percentTooHigh: 'O percentual é alto demais para calcular um preço. Informe um valor menor.',
} as const;

export interface PricingInput {
  /** Custo do item. `null` = não informado (o caso mais comum). */
  cost: number | null;
  /** Percentual digitado, já convertido. `null` = não informado. */
  percent: number | null;
  mode: PricingMode;
}

/**
 * A conta completa da calculadora: valida, calcula o preço e devolve SEMPRE os
 * dois indicadores, para o usuário ver a diferença entre markup e margem no
 * mesmo resultado.
 */
export function computePricing({ cost, percent, mode }: PricingInput): PricingResult {
  const empty: PricingResult = {
    ok: false,
    error: null,
    price: null,
    profit: null,
    markupPercent: null,
    marginPercent: null,
  };

  if (cost == null) return { ...empty, error: PRICING_ERRORS.noCost };
  if (!Number.isFinite(cost) || cost < 0) return { ...empty, error: PRICING_ERRORS.invalidCost };
  // Custo zero: markup é divisão por zero e a margem é 100% para qualquer
  // preço. Nenhum percentual determina um preço, então não há o que calcular.
  if (cost === 0) return { ...empty, error: PRICING_ERRORS.zeroCost };

  if (percent == null) return { ...empty, error: PRICING_ERRORS.noPercent };
  if (!Number.isFinite(percent)) return { ...empty, error: PRICING_ERRORS.noPercent };
  if (percent < 0) return { ...empty, error: PRICING_ERRORS.negativePercent };
  if (mode === 'margin' && percent >= 100) return { ...empty, error: PRICING_ERRORS.marginTooHigh };

  const price = mode === 'markup' ? priceFromMarkup(cost, percent) : priceFromMargin(cost, percent);

  // O RESULTADO também precisa ser finito, não só a entrada.
  //
  // As guardas acima checam `cost` e `percent`, mas um percentual FINITO e
  // enorme (o usuário segurando o 9 no campo) estoura na multiplicação:
  // `100 × (1 + 1e308/100)` transborda para Infinity. Sem esta linha, a função
  // devolvia `ok: true` com `price: Infinity` — violando a promessa escrita no
  // topo deste arquivo ("nunca `Infinity` nem `NaN` — que iriam parar na tela")
  // e permitindo gravar um preço não-finito no catálogo.
  if (!Number.isFinite(price)) return { ...empty, error: PRICING_ERRORS.percentTooHigh };

  // O lucro sai do preço ARREDONDADO, senão a tela mostraria venda 149,99 com
  // lucro de 50,00 e o usuário faria a conta na mão e acharia um centavo perdido.
  const profit = roundMoney(price - cost);

  return {
    ok: true,
    error: null,
    price,
    profit,
    markupPercent: markupFromPrice(cost, price),
    marginPercent: marginFromPrice(cost, price),
  };
}
